import { useState, useEffect } from 'react';
import { formatSessionWallTime } from '@lighthouse/shared';
import { adminApi, ensureArray } from '../services/api';

interface ReportedSession {
  id: string;
  scheduledAt: string;
  status: string;
  amountNgn: number;
  client: { user: { email: string } };
  therapist: { user: { email: string }; bio: string };
  payment: { status: string };
}

export function SessionsPage() {
  const [sessions, setSessions] = useState<ReportedSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    adminApi<ReportedSession[]>('/admin/sessions/reported')
      .then((data) => setSessions(ensureArray(data)))
      .catch((err) => {
        setSessions([]);
        setError((err as Error).message || 'Failed to load sessions');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleRefund = async (id: string) => {
    try {
      await adminApi(`/admin/refund/${id}`, { method: 'POST' });
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError((err as Error).message || 'Refund failed');
    }
  };

  if (loading) return <div className="text-center text-gray-500">Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Session Moderation</h1>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {sessions.length === 0 ? (
        <div className="card mt-6 text-center text-gray-500">No reported sessions.</div>
      ) : (
        <div className="mt-6 space-y-4">
          {sessions.map((s) => (
            <div key={s.id} className="card flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {s.client.user.email} ↔ {s.therapist.user.email}
                </p>
                <p className="text-sm text-gray-500">
                  {formatSessionWallTime(s.scheduledAt, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}{' '}
                  · ₦{s.amountNgn.toLocaleString()} · {s.status}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleRefund(s.id)}
                className="btn-primary text-sm"
              >
                Issue Refund
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
